import React, { useEffect, useState, useMemo } from 'react';
import useMeta from '../hooks/useMeta';
import { metaApi } from '../services/api';
import toast from 'react-hot-toast';

const statusColors = {
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  INACTIVE: 'bg-slate-100 text-slate-500',
  ARCHIVED: 'bg-slate-100 text-slate-500',
  DELETED: 'bg-red-100 text-red-700',
};

const statusTranslation = {
  ACTIVE: 'ATIVO',
  INACTIVE: 'INATIVO',
  ARCHIVED: 'ARQUIVADO',
  DELETED: 'EXCLUÍDO',
};

const sourceBadge = {
  landing_page: { label: 'Landing Page', className: 'bg-violet-100 text-violet-800' },
  meta_ads: { label: 'Meta Ads', className: 'bg-blue-100 text-blue-800' },
};

const LEADS_PAGE_SIZE = 50;

// 'YYYY-MM-DD' in local time (not UTC — toISOString() would shift near midnight
// depending on the user's timezone, off-by-one-day-ing the default range).
const toDateInputValue = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const currentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: toDateInputValue(start), end: toDateInputValue(end) };
};

const Leads = () => {
  const { pages, leadForms, loading, error, fetchPages, fetchLeadForms, fetchFormLeads, fetchAllLeads } = useMeta();
  const [selectedPage, setSelectedPage] = useState('');
  const [pagesLoading, setPagesLoading] = useState(false);
  const [formsLoading, setFormsLoading] = useState(false);

  const [viewMode, setViewMode] = useState('all'); // 'forms' | 'all' — 'all' is the default view
  const [allLeads, setAllLeads] = useState([]);
  const [allLeadsTotal, setAllLeadsTotal] = useState(0);
  const [allLeadsPage, setAllLeadsPage] = useState(1);
  const [allLeadsTotalPages, setAllLeadsTotalPages] = useState(0);
  const [allLeadsLoading, setAllLeadsLoading] = useState(false);
  const [pageFilter, setPageFilter] = useState('');
  const defaultRange = currentMonthRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [exportingXlsx, setExportingXlsx] = useState(false);

  const [selectedFormForLeads, setSelectedFormForLeads] = useState(null);
  const [formLeads, setFormLeads] = useState([]);
  const [formLeadsTotal, setFormLeadsTotal] = useState(0);
  const [formLeadsPage, setFormLeadsPage] = useState(1);
  const [formLeadsTotalPages, setFormLeadsTotalPages] = useState(0);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [exportingFormXlsx, setExportingFormXlsx] = useState(false);

  const loadFormLeads = async (form, page = 1) => {
    setLeadsLoading(true);
    try {
      const data = await fetchFormLeads(form.id, { page, limit: LEADS_PAGE_SIZE });
      setFormLeads(data.leads || []);
      setFormLeadsTotal(data.total || 0);
      setFormLeadsPage(data.page || 1);
      setFormLeadsTotalPages(data.totalPages || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleViewLeads = async (form) => {
    setSelectedFormForLeads(form);
    setFormLeads([]);
    await loadFormLeads(form, 1);
  };

  const closeLeadsModal = () => {
    setSelectedFormForLeads(null);
  };

  const handleExportCSV = (data, defaultFilename) => {
    if (!data || data.length === 0) return;

    const keys = new Set();
    data.forEach(lead => Object.keys(lead).forEach(k => keys.add(k)));
    const filterKeys = Array.from(keys).filter(k => !['id', 'pageId', 'createdAt', 'isExported', 'source'].includes(k));

    const headers = ['Data', ...filterKeys.map(k => k.replace(/_/g, ' '))];

    const rows = data.map(row => {
      const dateStr = new Date(row.createdAt).toLocaleString('pt-BR');
      const rowValues = filterKeys.map(k => {
        let val = row[k] === null || row[k] === undefined ? '' : String(row[k]);
        val = val.replace(/"/g, '""');
        return `"${val}"`;
      });
      return [`"${dateStr}"`, ...rowValues].join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStamp = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `${defaultFilename}-${dateStamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportXLSX = async () => {
    setExportingXlsx(true);
    try {
      const params = {};
      if (pageFilter) params.pageId = pageFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      await metaApi.exportAllLeads(params);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Falha ao exportar XLSX');
    } finally {
      setExportingXlsx(false);
    }
  };

  const handleExportFormXLSX = async () => {
    if (!selectedFormForLeads) return;
    setExportingFormXlsx(true);
    try {
      const filename = `leads-${selectedFormForLeads.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.xlsx`;
      await metaApi.exportFormLeads(selectedFormForLeads.id, filename);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Falha ao exportar XLSX');
    } finally {
      setExportingFormXlsx(false);
    }
  };

  useEffect(() => {
    setPagesLoading(true);
    fetchPages()
      .then((pgs) => {
        if (pgs?.length > 0 && !selectedPage) {
          const firstId = pgs[0].id;
          setSelectedPage(firstId);
          loadForms(firstId);
        }
      })
      .finally(() => setPagesLoading(false));
  }, []);

  useEffect(() => {
    if (viewMode === 'all') {
      loadAllLeads(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, pageFilter, startDate, endDate]);

  const loadAllLeads = async (page = 1) => {
    setAllLeadsLoading(true);
    try {
      const params = { page, limit: LEADS_PAGE_SIZE };
      if (pageFilter) params.pageId = pageFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const data = await fetchAllLeads(params);
      setAllLeads(data.leads || []);
      setAllLeadsTotal(data.total || 0);
      setAllLeadsPage(data.page || 1);
      setAllLeadsTotalPages(data.totalPages || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setAllLeadsLoading(false);
    }
  };

  const loadForms = async (pageId) => {
    if (!pageId) return;
    setFormsLoading(true);
    try {
      await fetchLeadForms(pageId);
    } finally {
      setFormsLoading(false);
    }
  };

  const handlePageChange = (e) => {
    const pageId = e.target.value;
    setSelectedPage(pageId);
    loadForms(pageId);
  };

  const handleRefresh = () => {
    if (viewMode === 'forms') {
      if (selectedPage) loadForms(selectedPage);
    } else {
      loadAllLeads(allLeadsPage);
    }
  };

  const allLeadKeys = useMemo(() => {
    const keys = new Set();
    allLeads.forEach(lead => {
      Object.keys(lead).forEach(k => keys.add(k));
    });
    return Array.from(keys).filter(k => !['id', 'pageId', 'formName', 'createdAt', 'isExported', 'source'].includes(k));
  }, [allLeads]);

  return (
    <div className="animate-fade-in pb-16">
      <div className="mb-12 flex justify-between items-end">
        <div>
          <h1 className="font-headline font-extrabold text-4xl tracking-tight text-on-surface mb-2">Leads</h1>
          <p className="text-secondary font-body">Gerencie e exporte seus leads dos formulários do Meta e landing pages</p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => handleExportCSV(allLeads, 'leads_pagina_atual')}
            className="px-6 py-2 rounded-full border border-outline-variant text-sm font-semibold hover:bg-white transition-all text-on-surface flex items-center gap-2 disabled:opacity-50"
            disabled={viewMode === 'forms' || allLeads.length === 0}
            title={viewMode === 'forms' ? "Mude para a visão 'Todos os Leads' para exportar" : "Exporta os leads carregados nesta página (CSV)"}
          >
            <span className="material-symbols-outlined text-sm">download</span> Exportar CSV
          </button>
          <button
            onClick={handleExportXLSX}
            className="px-6 py-2 rounded-full border border-outline-variant text-sm font-semibold hover:bg-white transition-all text-on-surface flex items-center gap-2 disabled:opacity-50"
            disabled={viewMode === 'forms' || exportingXlsx || allLeadsTotal === 0}
            title="Exporta TODOS os leads (respeitando o filtro de página), sem limite de paginação"
          >
            <span className="material-symbols-outlined text-sm">table_view</span> {exportingXlsx ? 'Exportando...' : 'Exportar XLSX (tudo)'}
          </button>
          <button onClick={handleRefresh} disabled={formsLoading || allLeadsLoading || !selectedPage} className="primary-pill-gradient text-white px-6 py-2 rounded-full text-sm font-manrope font-semibold hover:opacity-90 transition-all font-headline flex items-center gap-2 disabled:opacity-50">
            <span className="material-symbols-outlined text-sm">refresh</span> {formsLoading ? 'Carregando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl bg-error-container border border-error/20 p-6 mb-8 flex items-start gap-4">
           <span className="material-symbols-outlined text-error mt-0.5">error</span>
           <div>
              <p className="text-sm font-bold text-on-error-container mb-1">{error}</p>
              <p className="text-sm text-on-error-container/80 font-body mt-1">
                 Verifique se sua conta Meta está conectada e se a página está vinculada.
              </p>
           </div>
        </div>
      )}

      {/* Page selector */}
      {viewMode === 'forms' && pages.length > 0 && (
        <div className="bg-surface-container-lowest p-6 mb-8 rounded-xl shadow-[0_8px_32px_rgba(25,28,30,0.04)] flex flex-col md:flex-row items-center gap-4">
          <label className="text-sm font-bold text-on-surface font-headline whitespace-nowrap">Página Selecionada:</label>
          <select
            value={selectedPage}
            onChange={handlePageChange}
            className="input max-w-sm"
            disabled={pagesLoading}
          >
            {pages.map((page) => (
              <option key={page.id} value={page.id}>
                {page.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="bg-surface-container-low rounded-xl p-10">

        <div className="flex bg-surface-container rounded-lg p-1 w-fit mb-8">
          <button
            onClick={() => setViewMode('forms')}
            className={`px-4 py-2 font-bold text-sm rounded-md transition-all ${viewMode === 'forms' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Visão por Formulário
          </button>
          <button
            onClick={() => setViewMode('all')}
            className={`px-4 py-2 font-bold text-sm rounded-md transition-all ${viewMode === 'all' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Todos os Leads
          </button>
        </div>

        {viewMode === 'forms' && (
          <>
            <h2 className="font-headline font-bold text-2xl text-on-surface mb-8">
              Formulários de Lead
              {leadForms.length > 0 && (
                <span className="text-sm font-normal text-slate-400 ml-2">({leadForms.length} formulários)</span>
              )}
            </h2>

            {(pagesLoading || formsLoading) ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse h-20 bg-surface-container-highest/20 rounded-xl" />
            ))}
          </div>
        ) : pages.length === 0 ? (
          <div className="bg-surface-container-lowest rounded-xl p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-surface-container flex items-center justify-center mx-auto mb-6 text-slate-400">
               <span className="material-symbols-outlined text-3xl">pages</span>
            </div>
            <p className="font-headline font-bold text-xl text-slate-800">Nenhuma página encontrada</p>
            <p className="text-sm mt-2 text-slate-500 max-w-sm mx-auto">Conecte sua conta do Meta e certifique-se de gerenciar uma Página do Facebook.</p>
          </div>
        ) : leadForms.length === 0 ? (
          <div className="bg-surface-container-lowest rounded-xl p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-surface-container flex items-center justify-center mx-auto mb-6 text-slate-400">
               <span className="material-symbols-outlined text-3xl">assignment</span>
            </div>
            <p className="font-headline font-bold text-xl text-slate-800">Nenhum formulário encontrado para esta página</p>
            <p className="text-sm mt-2 text-slate-500 max-w-sm mx-auto">Crie uma campanha de Geração de Cadastros no Meta com um formulário anexado a esta página.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome do Formulário</th>
                  <th>ID do Formulário</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {leadForms.map((form) => (
                  <tr key={form.id} className="bg-surface-container-lowest group hover:shadow-md transition-shadow">
                    <td className="px-6 py-6 rounded-l-xl">
                      <p className="font-headline font-bold text-sm text-on-surface">{form.name}</p>
                    </td>
                    <td className="px-6 py-6">
                      <span className="font-mono text-sm text-secondary">{form.id}</span>
                    </td>
                    <td className="px-6 py-6">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[form.status] || 'bg-slate-100 text-slate-600'}`}>
                        {statusTranslation[form.status] || form.status || 'DESCONHECIDO'}
                      </span>
                    </td>
                    <td className="px-6 py-6 rounded-r-xl text-right">
                      <button
                        onClick={() => handleViewLeads(form)}
                        className="text-primary font-bold text-sm hover:underline flex items-center justify-end gap-1 w-full text-right"
                      >
                        Ver Leads
                        <span className="material-symbols-outlined text-sm">arrow_forward_ios</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}

        {viewMode === 'all' && (
          <>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
              <h2 className="font-headline font-bold text-2xl text-on-surface">
                Todos os Leads Recebidos
                {allLeadsTotalPages > 1 && (
                  <span className="text-sm font-normal text-slate-400 ml-2">
                    (página {allLeadsPage} de {allLeadsTotalPages})
                  </span>
                )}
              </h2>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-on-surface whitespace-nowrap">De</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="input"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-on-surface whitespace-nowrap">Até</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="input"
                  />
                </div>
                <button
                  onClick={() => { const r = currentMonthRange(); setStartDate(r.start); setEndDate(r.end); }}
                  className="px-4 py-2 rounded-full border border-outline-variant text-xs font-semibold hover:bg-white transition-all text-on-surface whitespace-nowrap"
                  title="Volta o período para o mês atual"
                >
                  Mês atual
                </button>
                {pages.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-on-surface whitespace-nowrap">Página</label>
                    <select
                      value={pageFilter}
                      onChange={e => setPageFilter(e.target.value)}
                      className="input max-w-xs"
                    >
                      <option value="">Todas as páginas</option>
                      {pages.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Resumo do período filtrado */}
            <div className="bg-surface-container-lowest rounded-xl p-6 mb-8 flex items-center gap-4 shadow-[0_8px_32px_rgba(25,28,30,0.04)]">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                <span className="material-symbols-outlined">group</span>
              </div>
              <div>
                <p className="text-sm text-secondary font-body">
                  Leads recebidos {startDate && endDate ? `de ${new Date(startDate + 'T00:00:00').toLocaleDateString('pt-BR')} até ${new Date(endDate + 'T00:00:00').toLocaleDateString('pt-BR')}` : 'no período'}
                </p>
                <p className="font-headline font-extrabold text-3xl text-on-surface">
                  {allLeadsLoading ? '…' : allLeadsTotal}
                </p>
              </div>
            </div>

            {allLeadsLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                <p className="text-slate-500 font-medium">Buscando leads...</p>
              </div>
            ) : allLeads.length === 0 ? (
              <div className="text-center py-20 bg-surface-container-lowest rounded-xl">
                <span className="material-symbols-outlined text-4xl text-slate-300 mb-3">person_off</span>
                <p className="text-slate-500 font-medium">{allLeadsTotal === 0 ? 'Nenhum lead encontrado no sistema.' : 'Nenhum lead para a página selecionada.'}</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-xl border border-outline-variant">
                  <table className="w-full text-left text-sm text-slate-600">
                    <thead className="bg-surface-container-lowest text-slate-800 font-semibold border-b border-outline-variant text-xs uppercase tracking-wider">
                      <tr>
                        <th className="px-6 py-4 whitespace-nowrap">Data</th>
                        <th className="px-6 py-4 whitespace-nowrap">Origem</th>
                        <th className="px-6 py-4 whitespace-nowrap">Formulário</th>
                        {allLeadKeys.map(key => (
                          <th key={key} className="px-6 py-4 whitespace-nowrap">{key.replace(/_/g, ' ')}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant">
                      {allLeads.map((lead, i) => {
                        const badge = sourceBadge[lead.source] || sourceBadge.meta_ads;
                        return (
                          <tr key={i} className="hover:bg-surface-container-lowest transition-colors bg-white">
                            <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500">
                              {new Date(lead.createdAt).toLocaleString('pt-BR')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${badge.className}`}>
                                {badge.label}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="font-medium text-slate-700">{lead.formName}</span>
                            </td>
                            {allLeadKeys.map(key => (
                              <td key={key} className="px-6 py-4 text-slate-700 max-w-[200px] truncate" title={lead[key] || '-'}>
                                {lead[key] || '-'}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {allLeadsTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <button
                      onClick={() => loadAllLeads(allLeadsPage - 1)}
                      disabled={allLeadsPage <= 1 || allLeadsLoading}
                      className="px-4 py-2 rounded-full border border-outline-variant text-sm font-semibold hover:bg-white transition-all disabled:opacity-40"
                    >
                      Anterior
                    </button>
                    <span className="text-sm text-slate-500">Página {allLeadsPage} de {allLeadsTotalPages}</span>
                    <button
                      onClick={() => loadAllLeads(allLeadsPage + 1)}
                      disabled={allLeadsPage >= allLeadsTotalPages || allLeadsLoading}
                      className="px-4 py-2 rounded-full border border-outline-variant text-sm font-semibold hover:bg-white transition-all disabled:opacity-40"
                    >
                      Próxima
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {/* Leads Modal */}
      {selectedFormForLeads && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-8 py-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <div>
                <h2 className="text-xl font-headline font-bold text-slate-800">Leads Recebidos</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Formulário: <span className="font-semibold text-slate-700">{selectedFormForLeads.name}</span>
                  {formLeadsTotal > 0 && (
                    <span className="text-slate-400"> · {formLeadsTotal} lead{formLeadsTotal === 1 ? '' : 's'} no total{formLeadsTotalPages > 1 ? ` · página ${formLeadsPage} de ${formLeadsTotalPages}` : ''}</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleExportCSV(formLeads, `formulario_${selectedFormForLeads.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}`)}
                  disabled={formLeads.length === 0}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-white transition-all text-slate-700 flex items-center gap-2 disabled:opacity-50 bg-white shadow-sm"
                >
                  <span className="material-symbols-outlined text-sm">download</span> CSV
                </button>
                <button
                  onClick={handleExportFormXLSX}
                  disabled={exportingFormXlsx || formLeadsTotal === 0}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-white transition-all text-slate-700 flex items-center gap-2 disabled:opacity-50 bg-white shadow-sm"
                >
                  <span className="material-symbols-outlined text-sm">table_view</span> {exportingFormXlsx ? 'Exportando...' : 'XLSX (tudo)'}
                </button>
                <button
                  onClick={closeLeadsModal}
                  className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-colors"
                >
                  <span className="material-symbols-outlined text-xl">close</span>
                </button>
              </div>
            </div>

            <div className="p-8 overflow-y-auto flex-1 bg-white">
              {leadsLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
                  <p className="text-slate-500 font-medium">Buscando leads...</p>
                </div>
              ) : formLeads.length === 0 ? (
                <div className="text-center py-20 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <span className="material-symbols-outlined text-4xl text-slate-300 mb-3">person_off</span>
                  <p className="text-slate-500 font-medium">Nenhum lead encontrado para este formulário.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-left text-sm text-slate-600">
                      <thead className="bg-slate-50 text-slate-800 font-semibold border-b border-slate-200 text-xs uppercase tracking-wider">
                        <tr>
                          <th className="px-6 py-4">Data</th>
                          {Object.keys(formLeads[0])
                            .filter(key => !['id', 'isExported', 'createdAt', 'source', 'pageId', 'formName'].includes(key))
                            .map(key => (
                            <th key={key} className="px-6 py-4 whitespace-nowrap">{key.replace(/_/g, ' ')}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {formLeads.map((lead, i) => (
                          <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500">
                              {new Date(lead.createdAt).toLocaleString('pt-BR')}
                            </td>
                            {Object.keys(formLeads[0])
                              .filter(key => !['id', 'isExported', 'createdAt', 'source', 'pageId', 'formName'].includes(key))
                              .map(key => (
                                <td key={key} className="px-6 py-4 text-slate-700">
                                  {lead[key]}
                                </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {formLeadsTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-6">
                      <button
                        onClick={() => loadFormLeads(selectedFormForLeads, formLeadsPage - 1)}
                        disabled={formLeadsPage <= 1 || leadsLoading}
                        className="px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold hover:bg-slate-50 transition-all disabled:opacity-40"
                      >
                        Anterior
                      </button>
                      <span className="text-sm text-slate-500">Página {formLeadsPage} de {formLeadsTotalPages}</span>
                      <button
                        onClick={() => loadFormLeads(selectedFormForLeads, formLeadsPage + 1)}
                        disabled={formLeadsPage >= formLeadsTotalPages || leadsLoading}
                        className="px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold hover:bg-slate-50 transition-all disabled:opacity-40"
                      >
                        Próxima
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Leads;
